import { defaultValues, handleInitialValues, validationSchema } from 'features/PromoCodesForm/utils';
import styles from './PromoCodesForm.module.css';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { DefaultValuesTypes } from 'features/PromoCodesForm/types';
import { PromoCode } from 'utils/types';
import { Form } from 'features/common/components/Form';
import { DiscountToggle } from 'features/common/components/Punchup/composites/forms/DiscountToggle';
import { EventPromoCodeFormParams } from './types';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupDateTimePicker } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupDateTimePicker/PunchupDateTimePicker';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupFormToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFormToggle';
import { useSeededEditForm } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { TicketTag } from 'features/PromoCodesForm/components/SpecificPromocodes/TicketTag';
import { getEvent } from 'features/PunchupPages/SendTickets/services';
import { useEditPromoCodeApi } from 'hooks/useEditPromoCode';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

// Discount-type literals + the promo-code attachment scope — the API contract values, named once.
const CUPON_PERCENTAGE = 'percentage';
const CUPON_FLAT = 'flat';
const PROMO_SCOPE_TICKET_TYPE = 'ticket_type';

const PromoCodesForm: React.FC = () => {
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { pathname } = useLocation();
	const { eventId } = useParams<EventPromoCodeFormParams>();
	const { promocode, isPromocodeError, mutateCreatePromoCode, mutateEditPromoCode } = useEditPromoCodeApi();

	const { data: event } = useQuery({
		queryKey: ['promoCodeEvent', eventId],
		queryFn: () => getEvent(eventId),
		enabled: !!eventId,
	});

	const isEdit = pathname.includes('edit');

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema()),
	});

	const { watch, setValue } = formMethods;

	// Seed the edit form from the fetched promo code exactly once (a refetch can't clobber in-progress
	// edits) and hold it behind a spinner until then so the toggles/date pickers don't blink defaults.
	// Replaces the prior `useMemo(() => formMethods.reset(...))`, which ran a side effect during render.
	const { isSeeded, seedGate } = useSeededEditForm<PromoCode, DefaultValuesTypes>({
		isEditMode: isEdit,
		formMethods,
		seedData: promocode as PromoCode | undefined,
		toFormValues: handleInitialValues,
		// Fail open: if the promo-code fetch errors, release the form (with defaults) instead of spinning forever.
		isError: isPromocodeError,
	});

	const isEndDate = watch('isEndDate');

	const onSubmit = (values: DefaultValuesTypes) => {
		if (isEdit && promocode) {
			mutateEditPromoCode({ values, type: PROMO_SCOPE_TICKET_TYPE, id: promocode.id });
		} else {
			mutateCreatePromoCode({ values, type: PROMO_SCOPE_TICKET_TYPE });
		}
	};

	const onChangeDiscount = () => {
		setValue('cuponType', watch('cuponType') === CUPON_PERCENTAGE ? CUPON_FLAT : CUPON_PERCENTAGE);
	};

	return (
		<PunchupPageShell pageName="Promo Code" onBack={goBack}>
			<PunchupPageContent>
				<PunchupPageHeader
					title={
						isEdit
							? t('promoCode-Form-edit-title', 'Edit promo code')
							: t('promoCode-Form-create-title', 'Create promo code')
					}
					description={t(
						'promoCode-Form-description',
						'Create a show-scoped discount code, set its value, and choose which ticket types and dates it applies to.'
					)}
				/>
				{!isSeeded ? (
					seedGate
				) : (
					<Form className={styles.styledForm} formMethods={formMethods} onSubmit={onSubmit}>
						<PunchupInput
							id="promocodeName"
							name="promocodeName"
							label={t('promoCode-Form-input-name')}
							title={t('promoCode-Form-input-name')}
							placeholder={t('promoCode-Form-input-name')}
						/>

						<FlexContainer gap="10px">
							<FlexContainer flex="1">
								<PunchupInput
									id="discountPercentage"
									name="discountPercentage"
									label={t('promoCode-Form-input-discount')}
									title={t('promoCode-Form-input-discount')}
									placeholder={t('promoCode-Form-input-discount')}
								/>
							</FlexContainer>

							<DiscountToggle
								label={t('promoCode-Form-discount-type', 'Type')}
								isPercentage={watch('cuponType') === CUPON_PERCENTAGE}
								handleChange={onChangeDiscount}
								name="toggle"
							/>
						</FlexContainer>

						<PunchupDateTimePicker
							id="startDate"
							name="startDate[0].start_date"
							label={t('ticketDetails-ticket-active-date')}
						/>

						<FlexContainer flexDirection="column" alignItems="stretch" gap="10px">
							<FlexContainer gap="10px" alignItems="center">
								<label className={styles.label}>{t('ticketDetails-ticket-stop-date')}</label>
								<FlexContainer>
									<PunchupFormToggle htmlFor="isEndDate" name="isEndDate" />
								</FlexContainer>
							</FlexContainer>

							{isEndDate && <PunchupDateTimePicker id="endDate" name="endDate[0].end_date" />}
						</FlexContainer>

						<FlexContainer flexDirection="column" gap="10px" marginTop="16px">
							<label className={styles.label}>{t('promoCode-Form-unlimited-ticketsQuantity-title')}</label>

							{event?.ticketTypes?.map((ticketType, index, tickets) => (
								<FlexContainer flexDirection="column" key={ticketType.ticketTypeId}>
									<TicketTag
										title={ticketType.type}
										isLastElement={index === tickets.length - 1}
										id={ticketType.ticketTypeId}
										unlimitedAll={false}
									/>
								</FlexContainer>
							))}
						</FlexContainer>

						<FlexContainer gap="10px" justifyContent="center">
							<PunchupButton
								text={t('button-cancel', 'Cancel')}
								variant="secondary"
								type="button"
								onClick={() => goBack()}
							/>
							<PunchupButton text={t('button-save', 'Save')} type="submit" />
						</FlexContainer>
					</Form>
				)}
			</PunchupPageContent>
		</PunchupPageShell>
	);
};

export { PromoCodesForm };
